import { isMapStyleReady } from '../mapcore.js';
// Layer derivati a punti v2 — port 1:1 dei layer v1 (map-derived-layers.js +
// map-layers.js airObs): Pollini CAMS, Qualità aria CAMS, Terremoti INGV,
// Aria misurata OpenAQ, Mareografi IOC/ISPRA, Argo/Drifter OGS, Segnalazioni
// pioggia crowdsourcing. Rendering MapLibre: una source GeoJSON + un circle
// layer per key (paint interamente data-driven da properties pre-calcolate:
// raggi/colori/opacità IDENTICI ai circleMarker Leaflet della v1). Hover =
// tooltip breve (bindTooltip v1), click = popup completo (bindPopup v1).
// Refresh: pollen/air/quakes/airObs/tideGauges/argo rifetchano ad ogni
// riattivazione (come gli activate*Layer v1); rainReports ogni 60s con guard
// document.hidden. Tutto sta SOTTO i simboli stazione ('ws-stations-symbols').

const API_BASE = (window.WEATHERSICILY_API_BASE || 'https://api.weathersicily.it/v1').replace(/\/+$/, '');
const RAIN_REPORTS_URL = 'https://live.weathersicily.it/nowcast/report.php?action=map';
// v1 usa il path relativo 'assets/data/...' (root del sito); la v2 vive in
// /v2/ quindi il path va ancorato alla root.
const AIR_OBS_URL = '/assets/data/air-quality-obs.json';
const RAIN_REFRESH_MS = 60000;
const EMPTY = { type: 'FeatureCollection', features: [] };

// (2026-07-03: argo rimosso come in v1)
// (2026-07-11: aggiunti 4 feed sweep-6 osservativi che leggono snapshot JSON:
//  edoDrought = siccità CDI EDO per comune, emsActivations = emergenze mappate
//  Copernicus, aeronetDust = AOD misurato Lampedusa, imergRain = pioggia
//  satellitare GPM sul mare. NB sea-level NON incluso: era doppione dei Mareografi.)
export const DERIVED_KEYS = ['territory', 'pollen', 'air', 'quakes', 'airObs', 'tideGauges', 'rainReports',
    'edoDrought', 'emsActivations', 'aeronetDust', 'imergRain'];

const EDO_URL = '/assets/data/edo-drought.json';
const EMS_URL = '/assets/data/ems-activations.json';
const AERONET_URL = '/assets/data/aeronet-dust.json';
const IMERG_URL = '/assets/data/imerg-rain.json';

// ---------------------------------------------------------------------------
// helper (port 1:1 da map-derived-layers.js)
// ---------------------------------------------------------------------------

function escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, ch => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[ch]));
}

function finite(value) {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
}

async function fetchJson(url, timeoutMs) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs || 9000);
    try {
        const response = await fetch(url, { cache: 'no-store', credentials: 'omit', signal: controller.signal });
        const json = await response.json();
        if (!response.ok || json.success === false) throw new Error('API ' + response.status);
        return json.data || json;
    } finally {
        clearTimeout(timer);
    }
}

function fetchApi(path, timeoutMs) {
    return fetchJson(API_BASE + path, timeoutMs);
}

function formatRome(value) {
    if (!value) return 'n.d.';
    const iso = String(value).replace(' ', 'T');
    const date = new Date(/[zZ]|[+\-]\d\d:?\d\d$/.test(iso) ? iso : iso + 'Z');
    if (isNaN(date)) return 'n.d.';
    return new Intl.DateTimeFormat('it-IT', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Rome'
    }).format(date);
}

// ---------------------------------------------------------------------------
// costanti stile (valori IDENTICI alla v1)
// ---------------------------------------------------------------------------

const POLLEN_SPECIES_LABELS = {
    grass: 'Graminacee', olive: 'Olivo', ragweed: 'Ambrosia',
    mugwort: 'Artemisia', birch: 'Betulla', alder: 'Ontano'
};
const POLLEN_LEVELS = {
    0: { fill: '#e2e8f0', stroke: '#94a3b8', label: 'Assente' },
    1: { fill: '#84cc16', stroke: '#3f6212', label: 'Basso' },
    2: { fill: '#facc15', stroke: '#854d0e', label: 'Moderato' },
    3: { fill: '#f97316', stroke: '#9a3412', label: 'Alto' },
    4: { fill: '#dc2626', stroke: '#7f1d1d', label: 'Molto alto' }
};
function pollenColor(level) { return POLLEN_LEVELS[level] || POLLEN_LEVELS[0]; }

// EAQI ufficiale (European Air Quality Index) bande 1..6.
const AQI_BANDS = {
    1: { fill: '#50f0e6', stroke: '#1b9e94', label: 'Buona' },
    2: { fill: '#50ccaa', stroke: '#1f8e74', label: 'Discreta' },
    3: { fill: '#f0e641', stroke: '#a89c12', label: 'Moderata' },
    4: { fill: '#ff5050', stroke: '#a30000', label: 'Scadente' },
    5: { fill: '#960032', stroke: '#5a001e', label: 'Cattiva' },
    6: { fill: '#7d2181', stroke: '#4a0e4d', label: 'Pessima' }
};
const AIR_POLLUTANT_LABELS = {
    aqi: 'Indice EAQI', pm25: 'PM2.5', pm10: 'PM10',
    no2: 'NO₂', o3: 'O₃', uv: 'UV', dust: 'Polveri sahariane'
};
function aqiBand(band) { return AQI_BANDS[band] || AQI_BANDS[1]; }

function quakeColor(mag) {
    const m = finite(mag) ?? 0;
    if (m < 2) return { fill: '#94a3b8', stroke: '#475569' };
    if (m < 3) return { fill: '#fbbf24', stroke: '#b45309' };
    if (m < 4) return { fill: '#f97316', stroke: '#9a3412' };
    if (m < 5) return { fill: '#ef4444', stroke: '#991b1b' };
    return { fill: '#b91c1c', stroke: '#7f1d1d' };
}

// Aria MISURATA — centraline OpenAQ (v1 map-layers.js, colori/label identici;
// nota: la v1 usa 'Scarsa' per banda 4 qui, 'Scadente' nel layer CAMS).
const WS_AQI_COLORS = { 1: '#50f0e6', 2: '#50ccaa', 3: '#f0e641', 4: '#ff5050', 5: '#960032', 6: '#7d2181' };
const WS_AQI_LABEL = { 1: 'Buona', 2: 'Discreta', 3: 'Moderata', 4: 'Scarsa', 5: 'Cattiva', 6: 'Pessima' };
const WS_AQ_PNAME = { pm25: 'PM2.5', pm10: 'PM10', no2: 'NO₂', o3: 'O₃', so2: 'SO₂', co: 'CO' };

const TIDE_ALERT = {
    0: { fill: '#22b8cf', stroke: '#0b7285', label: 'Normale' },
    1: { fill: '#fab005', stroke: '#946200', label: 'Attenzione' },
    2: { fill: '#fd7e14', stroke: '#9a4c0a', label: 'Livello alto' },
    3: { fill: '#fa5252', stroke: '#a51111', label: 'Allerta' }
};
function tideAlert(level) { return TIDE_ALERT[finite(level) ?? 0] || TIDE_ALERT[0]; }

const RAIN_STATE_STYLE = {
    dry:     { fill: '#74c0fc', stroke: '#1971c2', label: 'Asciutto', emo: '☀️' },
    drizzle: { fill: '#4dabf7', stroke: '#1864ab', label: 'Pioviggine', emo: '🌦️' },
    rain:    { fill: '#4263eb', stroke: '#1e3a8a', label: 'Pioggia', emo: '🌧️' },
    heavy:   { fill: '#5f3dc4', stroke: '#3b2476', label: 'Pioggia forte', emo: '🌧️' },
    storm:   { fill: '#e8590c', stroke: '#9a3412', label: 'Temporale', emo: '⛈️' },
    hail:    { fill: '#ae3ec9', stroke: '#702082', label: 'Grandine', emo: '🧊' }
};
function rainStyle(s) { return RAIN_STATE_STYLE[s] || RAIN_STATE_STYLE.rain; }

// ---------------------------------------------------------------------------
// stato modulo
// ---------------------------------------------------------------------------

const state = {
    map: null,
    beforeId: undefined,     // i punti derivati stanno SOTTO i simboli stazione
    enabled: {},             // key → bool
    seq: {},                 // key → contatore anti-race (pattern *Seq v1)
    wired: {},               // key → interazioni già agganciate
    pendingIdle: {},         // key → retry once('idle') in corso
    pollenSpecies: 'all',
    airPollutant: 'aqi',
    airData: null,           // ultimo payload aria (un solo fetch, tutti gli inquinanti)
    quakesDays: 7,
    rainTimer: null,
    territoryTimer: null,
};
DERIVED_KEYS.forEach(k => { state.enabled[k] = false; state.seq[k] = 0; });

function srcId(key) { return 'ws-drv-' + key; }

// ---------------------------------------------------------------------------
// layer MapLibre (guard isStyleLoaded con retry su once('idle'), pattern storms.js)
// ---------------------------------------------------------------------------

function ensureLayer(key) {
    const map = state.map;
    if (map.getSource(srcId(key))) return true;
    if (!isMapStyleReady(map)) {
        if (!state.pendingIdle[key]) {
            state.pendingIdle[key] = true;
            map.once('idle', () => {
                state.pendingIdle[key] = false;
                if (state.enabled[key]) refresh(key).catch(() => {});
            });
        }
        return false;
    }
    map.addSource(srcId(key), { type: 'geojson', data: EMPTY });
    const before = map.getLayer('ws-stations-symbols') ? 'ws-stations-symbols' : state.beforeId;
    map.addLayer({
        id: srcId(key), type: 'circle', source: srcId(key),
        paint: {
            // La v1 usa L.circleMarker: opacity del bordo di default 1.0,
            // fillOpacity per-marker → circle-opacity data-driven.
            'circle-radius': ['get', 'r'],
            'circle-color': ['get', 'fill'],
            'circle-opacity': ['get', 'fillOp'],
            'circle-stroke-color': ['get', 'stroke'],
            'circle-stroke-width': ['get', 'sw'],
        },
    }, before);
    wireLayerInteractions(key);
    return true;
}

function setData(key, features) {
    const src = state.map && state.map.getSource(srcId(key));
    if (src) src.setData({ type: 'FeatureCollection', features });
}

function clearLayer(key) {
    setData(key, []);
}

// ---------------------------------------------------------------------------
// interazioni: hover = tooltip breve (bindTooltip v1), click = popup completo
// (bindPopup v1). Un solo popup hover condiviso tra tutti i layer derivati.
// ---------------------------------------------------------------------------

let hoverPopup = null;
let clickPopup = null;

function ensureHoverPopup() {
    if (!hoverPopup) {
        hoverPopup = new maplibregl.Popup({
            closeButton: false, closeOnClick: false, offset: 10,
            className: 'wsv2-tooltip-shell ws-derived-tooltip-shell', maxWidth: '280px',
        });
    }
    return hoverPopup;
}

function wireLayerInteractions(key) {
    if (state.wired[key]) return;
    state.wired[key] = true;
    const map = state.map;
    const lyr = srcId(key);
    map.on('mousemove', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.tt) return;
        map.getCanvas().style.cursor = 'pointer';
        ensureHoverPopup()
            .setLngLat(f.geometry.coordinates)
            .setHTML(`<div class="ws-derived-tooltip">${f.properties.tt}</div>`)
            .addTo(map);
    });
    map.on('mouseleave', lyr, () => {
        map.getCanvas().style.cursor = '';
        if (hoverPopup) hoverPopup.remove();
    });
    map.on('click', lyr, (e) => {
        const f = e.features && e.features[0];
        if (!f || !f.properties.pp) return;
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) clickPopup.remove();
        clickPopup = new maplibregl.Popup({
            closeButton: true, closeOnClick: true, offset: 12,
            // shell per-feature (es. airObs → card scura .ws-air-popup-shell v1)
            className: 'wsv2-tooltip-shell ' + (f.properties.shell || 'ws-temp-variation-popup-shell'),
            maxWidth: (f.properties.mw || 280) + 'px',
        }).setLngLat(f.geometry.coordinates).setHTML(f.properties.pp).addTo(map);
    });
}

// feature helper: coordinate + stile marker + tooltip/popup pre-renderizzati
function pointFeature(lat, lon, style, tt, pp, mw) {
    return {
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lon, lat] },
        properties: { ...style, tt, pp, mw },
    };
}

// ---------------------------------------------------------------------------
// POLLINI — /v1/maps/pollen?species=X&day=today (livelli 1-4)
// ---------------------------------------------------------------------------

// I nomi punto CAMS arrivano con gli underscore (es. "San_Piero_Patti") →
// li rendiamo leggibili con spazi.
const prettyName = (n, fb = 'Località') => {
    const s = String(n || '').replace(/_/g, ' ').trim();
    return s || fb;
};

function pollenPopup(p) {
    const sp = POLLEN_SPECIES_LABELS[p.species] || p.species || 'Pollini';
    const lv = pollenColor(p.level);
    const conc = finite(p.conc);
    return `
        <article class="ws-temp-variation-popup">
            <span>Pollini · CAMS</span>
            <h3>${escapeHtml(prettyName(p.name))}</h3>
            <strong>${escapeHtml(lv.label)}<small>liv. ${escapeHtml(p.level)}</small></strong>
            <dl>
                <div><dt>Specie dominante</dt><dd>${escapeHtml(sp)}</dd></div>
                ${conc !== null ? `<div><dt>Concentrazione</dt><dd>${conc.toFixed(1)} gr/m³</dd></div>` : ''}
                ${p.province ? `<div><dt>Provincia</dt><dd>${escapeHtml(p.province)}</dd></div>` : ''}
            </dl>
        </article>`;
}

async function refreshPollen() {
    const seq = ++state.seq.pollen;
    const data = await fetchApi(`/maps/pollen?species=${encodeURIComponent(state.pollenSpecies)}&day=today`);
    if (seq !== state.seq.pollen || !state.enabled.pollen) return;
    if (!ensureLayer('pollen')) return;
    const features = [];
    (Array.isArray(data.points) ? data.points : []).forEach(p => {
        const lat = finite(p.lat), lon = finite(p.lon), level = finite(p.level);
        if (lat === null || lon === null || level === null) return;
        const c = pollenColor(level);
        const sp = POLLEN_SPECIES_LABELS[p.species] || p.species || '';
        features.push(pointFeature(lat, lon, {
            r: level === 0 ? 3.2 : 5 + level * 1.6, fill: c.fill, stroke: c.stroke,
            sw: level === 0 ? 0.8 : 1.2, fillOp: level === 0 ? 0.5 : 0.85,
        }, `${escapeHtml(prettyName(p.name))}: ${escapeHtml(sp)} · ${escapeHtml(c.label)}`,
            pollenPopup(p), 290));
    });
    setData('pollen', features);
}

// ---------------------------------------------------------------------------
// QUALITÀ ARIA — /v1/maps/air-quality?pollutant=aqi&day=today (EAQI 1-6);
// un solo fetch, il cambio inquinante ri-renderizza soltanto (come la v1).
// ---------------------------------------------------------------------------

function airPopup(p) {
    const b = aqiBand(p.aqi_band);
    const row = (label, value, unit) => (finite(value) !== null)
        ? `<div><dt>${label}</dt><dd>${finite(value).toFixed(1)} ${unit}</dd></div>` : '';
    return `
        <article class="ws-temp-variation-popup">
            <span>Qualità aria · CAMS</span>
            <h3>${escapeHtml(prettyName(p.name))}</h3>
            <strong>${escapeHtml(p.aqi_label || b.label)}<small>EAQI ${escapeHtml(p.aqi_band ?? '–')}</small></strong>
            <dl>
                ${row('PM2.5', p.pm25, 'µg/m³')}
                ${row('PM10', p.pm10, 'µg/m³')}
                ${row('O₃', p.o3, 'µg/m³')}
                ${row('NO₂', p.no2, 'µg/m³')}
                ${finite(p.uv) !== null ? `<div><dt>UV</dt><dd>${finite(p.uv).toFixed(1)}</dd></div>` : ''}
                ${p.dust_level ? `<div><dt>Polveri Sahara</dt><dd>${p.dust_level >= 2 ? 'Forte' : 'Rilevante'}</dd></div>` : ''}
            </dl>
        </article>`;
}

function renderAirMarkers() {
    if (!state.airData || !state.enabled.air) return;
    if (!ensureLayer('air')) return;
    const label = AIR_POLLUTANT_LABELS[state.airPollutant] || '';
    const features = [];
    (Array.isArray(state.airData.points) ? state.airData.points : []).forEach(p => {
        const lat = finite(p.lat), lon = finite(p.lon);
        if (lat === null || lon === null) return;
        const b = aqiBand(p.aqi_band);
        const shown = state.airPollutant === 'aqi' ? p.aqi_band : finite(p[state.airPollutant]);
        const shownTxt = (shown === null || shown === undefined) ? 'n.d.'
            : (state.airPollutant === 'aqi' ? `EAQI ${shown}` : `${Number(shown).toFixed(1)}`);
        features.push(pointFeature(lat, lon, {
            r: 6, fill: b.fill, stroke: b.stroke, sw: 1.1, fillOp: 0.82,
        }, `${escapeHtml(prettyName(p.name))}: ${escapeHtml(label)} ${escapeHtml(shownTxt)}`,
            airPopup(p), 300));
    });
    setData('air', features);
}

async function refreshAir() {
    const seq = ++state.seq.air;
    const data = await fetchApi(`/maps/air-quality?pollutant=aqi&day=today`);
    if (seq !== state.seq.air) return;
    state.airData = data;
    renderAirMarkers();
}

// ---------------------------------------------------------------------------
// TERREMOTI — /v1/maps/quakes?days=N&limit=400 (INGV, raggio per magnitudo)
// ---------------------------------------------------------------------------

function quakePopup(p) {
    const mag = finite(p.magnitude);
    const depth = finite(p.depth);
    return `
        <article class="ws-temp-variation-popup">
            <span>Terremoto · INGV</span>
            <h3>M ${mag !== null ? mag.toFixed(1) : '–'} · ${escapeHtml(p.localita || '')}</h3>
            <dl>
                <div><dt>Profondità</dt><dd>${depth !== null ? depth.toFixed(0) + ' km' : 'n.d.'}</dd></div>
                <div><dt>Quando</dt><dd>${escapeHtml(formatRome(p.datetime_event))}</dd></div>
            </dl>
        </article>`;
}

async function refreshQuakes() {
    const seq = ++state.seq.quakes;
    const data = await fetchApi(`/maps/quakes?days=${state.quakesDays}&limit=400`);
    if (seq !== state.seq.quakes || !state.enabled.quakes) return;
    if (!ensureLayer('quakes')) return;
    const features = [];
    (Array.isArray(data.points) ? data.points : []).forEach(p => {
        const lat = finite(p.lat), lon = finite(p.lon), mag = finite(p.magnitude) ?? 0;
        if (lat === null || lon === null) return;
        const c = quakeColor(mag);
        features.push(pointFeature(lat, lon, {
            r: Math.max(4, 3 + mag * 2.4), fill: c.fill, stroke: c.stroke, sw: 1.4, fillOp: 0.7,
        }, `M ${mag.toFixed(1)} · ${escapeHtml(p.localita || '')}`, quakePopup(p), 280));
    });
    setData('quakes', features);
}

// ---------------------------------------------------------------------------
// ARIA MISURATA — centraline OpenAQ da /assets/data/air-quality-obs.json
// (cache-buster orario come la v1; nessun tooltip hover nella v1 → solo popup)
// ---------------------------------------------------------------------------

// Card dedicata .ws-air-popup (port ESATTO v1 activateAirObsLayer,
// map-layers.js:677-685: badge, fascia AQI colorata, griglia inquinanti).
function airObsPopup(s, col) {
    const plist = Object.entries(s.params || {}).map(([k, v]) =>
        `<div class="ws-air-popup__param"><span>${WS_AQ_PNAME[k] || k}</span><b>${Number(v).toFixed(1)}<i>µg/m³</i></b></div>`).join('');
    return `<article class="ws-air-popup">
        <span class="ws-air-popup__badge">Aria misurata · OpenAQ</span>
        <h3>${escapeHtml(s.name || 'Centralina')}</h3>
        <div class="ws-air-popup__band" style="--ws-aqi:${col}"><i></i>${WS_AQI_LABEL[s.aqi_band] || 'n.d.'}</div>
        ${plist ? `<div class="ws-air-popup__grid">${plist}</div>` : ''}
        <small>Ultima misura ${escapeHtml(s.obs_time || '')} · Fonte: OpenAQ (reti ARPA)</small></article>`;
}

async function refreshAirObs() {
    const seq = ++state.seq.airObs;
    const d = await fetchJson(AIR_OBS_URL + '?_=' + Math.floor(Date.now() / 3600000), 12000);
    if (seq !== state.seq.airObs || !state.enabled.airObs) return;
    if (!ensureLayer('airObs')) return;
    const features = [];
    (d.stations || []).forEach(s => {
        if (s.lat == null || s.lon == null) return;
        const col = WS_AQI_COLORS[s.aqi_band] || '#9aa';
        const f = pointFeature(Number(s.lat), Number(s.lon), {
            r: 6, fill: col, stroke: '#fff', sw: 1.2, fillOp: 0.92,
        }, '', airObsPopup(s, col), 300);
        f.properties.shell = 'ws-air-popup-shell';
        features.push(f);
    });
    setData('airObs', features);
}

// ---------------------------------------------------------------------------
// MAREOGRAFI — /v1/maps/tide-gauges (livello mare IOC + ISPRA TAD, soglie allerta)
// ---------------------------------------------------------------------------

function tidePopup(p) {
    const lvl = finite(p.level_m);
    const wt = finite(p.water_temp);
    const a = tideAlert(p.alert_level);
    return `
        <article class="ws-temp-variation-popup">
            <span>Mareografo · ${escapeHtml(p.source === 'ISPRA_TAD' ? 'ISPRA TAD' : 'IOC')}</span>
            <h3>${escapeHtml(p.name || 'Mareografo')}</h3>
            <strong>${lvl !== null ? (lvl > 0 ? '+' : '') + lvl.toFixed(3) + ' m' : 'n.d.'}<small>${escapeHtml(a.label)}</small></strong>
            <dl>
                ${wt !== null ? `<div><dt>Temp. acqua</dt><dd>${wt.toFixed(1)} °C</dd></div>` : ''}
                <div><dt>Osservazione</dt><dd>${escapeHtml(formatRome(p.obs_time))}</dd></div>
                ${finite(p.data_age_min) !== null ? `<div><dt>Aggiornato</dt><dd>${finite(p.data_age_min)} min fa</dd></div>` : ''}
            </dl>
        </article>`;
}

async function refreshTideGauges() {
    const seq = ++state.seq.tideGauges;
    const data = await fetchApi('/maps/tide-gauges');
    if (seq !== state.seq.tideGauges || !state.enabled.tideGauges) return;
    if (!ensureLayer('tideGauges')) return;
    const features = [];
    (Array.isArray(data.points) ? data.points : []).forEach(p => {
        const lat = finite(p.lat), lon = finite(p.lon);
        if (lat === null || lon === null) return;
        const a = tideAlert(p.alert_level);
        const lvl = finite(p.level_m);
        features.push(pointFeature(lat, lon, {
            r: 8, fill: a.fill, stroke: a.stroke, sw: 1.6, fillOp: 0.85,
        }, `${escapeHtml(p.name || 'Mareografo')}: ${lvl !== null ? (lvl > 0 ? '+' : '') + lvl.toFixed(2) + ' m' : 'n.d.'}`,
            tidePopup(p), 280));
    });
    setData('tideGauges', features);
}

// ---------------------------------------------------------------------------
// SEGNALAZIONI PIOGGIA — live.weathersicily.it/nowcast/report.php?action=map
// (crowdsourcing, finestra ~45', opacità calante con l'età, refresh 60s)
// ---------------------------------------------------------------------------

function rainReportsPopup(p) {
    const s = rainStyle(p.state);
    const n = finite(p.n) ?? 1;
    const age = finite(p.age_min);
    return `
        <article class="ws-temp-variation-popup">
            <span>Segnalazione utenti · live</span>
            <h3>${s.emo} ${escapeHtml(s.label)}</h3>
            <dl>
                <div><dt>Conferme</dt><dd>${n}</dd></div>
                <div><dt>Quando</dt><dd>${age !== null ? (age <= 0 ? 'adesso' : age + ' min fa') : 'di recente'}</dd></div>
            </dl>
        </article>`;
}

async function refreshTerritory() {
    const seq = ++state.seq.territory;
    const status = document.getElementById('territory-layer-status');
    try {
        const data = await fetchJson('/territorio/data.php', 12000);
        if (seq !== state.seq.territory || !state.enabled.territory) return;
        if (!Array.isArray(data.items) || !Array.isArray(data.sources)) throw new Error('Formato fonte non disponibile');
        const source = data.sources.find(s => s.id === 'cciss');
        const age = Date.now() - Date.parse(source?.fetched_at);
        const fresh = source?.refresh_status === 'ok' && Number.isFinite(age) && age >= -300000 && age <= source.stale_after_seconds * 1000;
        if (!ensureLayer('territory')) return;
        const rows = data.items.filter(e => e.source_id === 'cciss' && Array.isArray(e.coordinates) && e.coordinates.length === 2);
        const features = rows.map(e => {
            const [lat, lon] = e.coordinates;
            const popup = `<article class="ws-temp-variation-popup"><span>CCISS · ${fresh ? 'avvisi stradali' : 'dato non aggiornato'}</span><h3>${escapeHtml(e.title)}</h3><p>${escapeHtml(e.road_section || '')}</p><p>${fresh && e.event_status === 'source_active' ? 'In corso secondo la fonte' : 'Verificare lo stato alla fonte'}</p><p>Ultima acquisizione: ${escapeHtml(formatRome(source?.fetched_at))}</p><p>Il punto indica l’inizio del tratto. La presenza di pioggia sulla mappa non conferma la causa del disservizio.</p><a href="https://www.cciss.it/web/cciss/situazione-della-viabilita" target="_blank" rel="noopener noreferrer">Fonte CCISS</a> · <a href="/territorio/">Tutti gli avvisi</a></article>`;
            return pointFeature(lat, lon, {r:8, fill:fresh?'#e08028':'#8b969e', stroke:'#6c4928', sw:2, fillOp:0.9}, escapeHtml(e.title), popup, 300);
        });
        setData('territory', features);
        if (status) status.textContent = fresh ? `${rows.length} avvisi CCISS disponibili. Copertura parziale: non misura i tempi di viaggio.` : 'CCISS non aggiornato: verifica gli avvisi alla fonte.';
    } catch (error) {
        if (seq !== state.seq.territory || !state.enabled.territory) return;
        clearLayer('territory');
        if (status) status.textContent = 'Avvisi stradali non disponibili. Nessun dato non significa strada libera.';
    }
}

async function refreshRainReports() {
    const seq = ++state.seq.rainReports;
    const data = await fetchJson(RAIN_REPORTS_URL, 12000);
    if (seq !== state.seq.rainReports || !state.enabled.rainReports) return;
    if (!ensureLayer('rainReports')) return;
    const win = finite(data.window_min) || 45;
    const features = [];
    (Array.isArray(data.points) ? data.points : []).forEach(p => {
        const lat = finite(p.lat), lon = finite(p.lon);
        if (lat === null || lon === null) return;
        const s = rainStyle(p.state);
        const age = finite(p.age_min) ?? 0;
        const n = finite(p.n) ?? 1;
        const op = Math.max(0.35, 0.9 - (age / win) * 0.55);
        features.push(pointFeature(lat, lon, {
            r: Math.min(14, 7 + (n - 1) * 2), fill: s.fill, stroke: s.stroke, sw: 1.5, fillOp: op,
        }, `${s.emo} ${escapeHtml(s.label)}${n > 1 ? ' ×' + n : ''}`, rainReportsPopup(p), 260));
    });
    setData('rainReports', features);
}

// ---------------------------------------------------------------------------
// SICCITÀ EDO — Combined Drought Indicator per comune (/assets/data/edo-drought.json)
// Fonte: Copernicus European Drought Observatory (JRC). Classe 0-6 per comune.
// ---------------------------------------------------------------------------

const CDI_CLASS = {
    0: { fill: '#e6efe3', stroke: '#9bb39a', label: 'Nessuna siccità' },
    1: { fill: '#ffe082', stroke: '#a1740b', label: 'Watch — piogge scarse' },
    2: { fill: '#ff9800', stroke: '#9a4a06', label: 'Warning — suolo secco' },
    3: { fill: '#d32f2f', stroke: '#7f1d1d', label: 'Alert — vegetazione in stress' },
    4: { fill: '#80deea', stroke: '#00838f', label: 'Recupero umidità suolo' },
    5: { fill: '#4fc3f7', stroke: '#0277bd', label: 'Recupero vegetazione' },
    6: { fill: '#66bb6a', stroke: '#2e7d32', label: 'Recupero completo' },
};

function edoPopup(c, cls) {
    return `
        <article class="ws-temp-variation-popup">
            <span>Siccità · Copernicus EDO</span>
            <h3>${escapeHtml(c.name || 'Comune')}</h3>
            <strong style="font-size:20px">${escapeHtml(cls.label)}</strong>
            <dl>
                <div><dt>Provincia</dt><dd>${escapeHtml(c.province || '–')}</dd></div>
                <div><dt>Indicatore</dt><dd>CDI classe ${escapeHtml(c.cdi)}</dd></div>
            </dl>
            <small style="opacity:.7">Combined Drought Indicator (© European Union) · aggiorn. ogni 10 giorni</small>
        </article>`;
}

async function refreshEdoDrought() {
    const seq = ++state.seq.edoDrought;
    const d = await fetchJson(EDO_URL + '?_=' + Math.floor(Date.now() / 3600000), 12000);
    if (seq !== state.seq.edoDrought || !state.enabled.edoDrought) return;
    if (!ensureLayer('edoDrought')) return;
    const features = [];
    (d.comuni || []).forEach(c => {
        const lat = finite(c.lat), lon = finite(c.lon), cdi = finite(c.cdi);
        if (lat === null || lon === null || cdi === null) return;
        const cls = CDI_CLASS[cdi] || CDI_CLASS[0];
        const drought = cdi >= 1 && cdi <= 3;   // Watch/Warning/Alert = siccità attiva
        features.push(pointFeature(lat, lon, {
            r: cdi === 0 ? 3 : (drought ? 6 : 4.5), fill: cls.fill, stroke: cls.stroke,
            sw: cdi === 0 ? 0.7 : 1.2, fillOp: cdi === 0 ? 0.45 : 0.85,
        }, `${escapeHtml(c.name)}: ${escapeHtml(cls.label)}`, edoPopup(c, cls), 290));
    });
    setData('edoDrought', features);
}

// ---------------------------------------------------------------------------
// EMERGENZE MAPPATE — Copernicus EMS Rapid Mapping (/assets/data/ems-activations.json)
// Attivazioni per emergenze (alluvioni, incendi, frane…) in Italia; flag Sicilia.
// ---------------------------------------------------------------------------

function emsColor(category) {
    const c = String(category || '').toLowerCase();
    if (/flood|alluv|idraul/.test(c)) return { fill: '#2563eb', stroke: '#1e3a8a' };
    if (/fire|incend|wildfire/.test(c)) return { fill: '#e8590c', stroke: '#9a3412' };
    if (/land ?slide|frana|debris/.test(c)) return { fill: '#8d6e63', stroke: '#4e342e' };
    if (/quake|seism|sisma|earth/.test(c)) return { fill: '#795548', stroke: '#3e2723' };
    if (/storm|wind|cyclone|tempest/.test(c)) return { fill: '#6d28d9', stroke: '#4c1d95' };
    if (/volcan|vulcan|erupt/.test(c)) return { fill: '#c0392b', stroke: '#7f1d1d' };
    if (/snow|neve|ice|ghiac/.test(c)) return { fill: '#26c6da', stroke: '#00838f' };
    return { fill: '#607d8b', stroke: '#37474f' };
}

function emsPopup(a) {
    const when = a.event_time || a.activation_time;
    return `
        <article class="ws-temp-variation-popup">
            <span>Emergenza mappata · Copernicus EMS</span>
            <h3>${escapeHtml(a.name || a.code || 'Attivazione')}</h3>
            <strong style="font-size:18px">${escapeHtml(a.category || 'Evento')}</strong>
            <dl>
                <div><dt>Codice</dt><dd>${escapeHtml(a.code || '–')}</dd></div>
                <div><dt>Stato</dt><dd>${a.closed ? 'Chiusa' : 'Attiva'}</dd></div>
                <div><dt>Evento</dt><dd>${escapeHtml(formatRome(when))}</dd></div>
                ${finite(a.n_products) !== null ? `<div><dt>Prodotti</dt><dd>${finite(a.n_products)}</dd></div>` : ''}
            </dl>
            <small style="opacity:.7">Rapid Mapping (© European Union)${a.sicily ? ' · interessa la Sicilia' : ''}</small>
        </article>`;
}

async function refreshEmsActivations() {
    const seq = ++state.seq.emsActivations;
    const d = await fetchJson(EMS_URL + '?_=' + Math.floor(Date.now() / 3600000), 12000);
    if (seq !== state.seq.emsActivations || !state.enabled.emsActivations) return;
    if (!ensureLayer('emsActivations')) return;
    const features = [];
    (d.activations || []).forEach(a => {
        const lat = finite(a.lat), lon = finite(a.lon);
        if (lat === null || lon === null) return;
        const col = emsColor(a.category);
        features.push(pointFeature(lat, lon, {
            r: a.sicily ? 9 : 6.5, fill: col.fill, stroke: a.sicily ? '#ffffff' : col.stroke,
            sw: a.sicily ? 2 : 1.2, fillOp: a.closed ? 0.5 : 0.85,
        }, `${escapeHtml(a.category || 'Emergenza')} · ${escapeHtml(a.code || '')}`, emsPopup(a), 290));
    });
    setData('emsActivations', features);
}

// ---------------------------------------------------------------------------
// POLVERI SAHARIANE MISURATE — AERONET Lampedusa (/assets/data/aeronet-dust.json)
// AOD spettrale a terra: complementa l'overlay satellitare (RGB dust) con la MISURA.
// ---------------------------------------------------------------------------

function aeronetPopup(s) {
    const aod = finite(s.aod_500), ang = finite(s.angstrom_440_870);
    return `
        <article class="ws-temp-variation-popup">
            <span>Polveri · AERONET (NASA/ENEA)</span>
            <h3>${escapeHtml(s.site || 'Lampedusa')}</h3>
            <strong style="font-size:20px">${s.dust ? 'Polvere sahariana' : 'Aria pulita'}</strong>
            <dl>
                ${aod !== null ? `<div><dt>AOD 500nm</dt><dd>${aod.toFixed(2)}</dd></div>` : ''}
                ${ang !== null ? `<div><dt>Ångström</dt><dd>${ang.toFixed(2)}</dd></div>` : ''}
                ${s.obs_date ? `<div><dt>Data</dt><dd>${escapeHtml(s.obs_date)}</dd></div>` : ''}
            </dl>
            <small style="opacity:.7">AOD alto + Ångström basso = aerosol grossolano (sabbia)</small>
        </article>`;
}

async function refreshAeronetDust() {
    const seq = ++state.seq.aeronetDust;
    const d = await fetchJson(AERONET_URL + '?_=' + Math.floor(Date.now() / 3600000), 12000);
    if (seq !== state.seq.aeronetDust || !state.enabled.aeronetDust) return;
    if (!ensureLayer('aeronetDust')) return;
    const features = [];
    (d.stations || []).forEach(s => {
        const lat = finite(s.lat), lon = finite(s.lon);
        if (lat === null || lon === null) return;
        const aod = finite(s.aod_500) ?? 0;
        const fill = s.dust ? '#b45309' : (aod > 0.15 ? '#d6a35c' : '#9ca3af');
        features.push(pointFeature(lat, lon, {
            r: Math.max(7, Math.min(16, 7 + aod * 12)), fill, stroke: '#4a2f10',
            sw: 1.4, fillOp: 0.8,
        }, `Polveri: ${s.dust ? 'sì' : 'no'} (AOD ${aod.toFixed(2)})`, aeronetPopup(s), 280));
    });
    setData('aeronetDust', features);
}

// ---------------------------------------------------------------------------
// PIOGGIA SATELLITE — GPM IMERG 0.1° sul mare (/assets/data/imerg-rain.json)
// Copre il mare aperto dove radar/pluviometri non arrivano. Solo celle BAGNATE.
// ---------------------------------------------------------------------------

function imergRainColor(mm) {
    if (mm < 1) return '#b3e5fc';
    if (mm < 5) return '#4fc3f7';
    if (mm < 10) return '#2196f3';
    if (mm < 25) return '#3f51b5';
    if (mm < 50) return '#7b1fa2';
    return '#4a148c';
}

function imergPopup(cell, date) {
    const pl = finite(cell.pl);
    return `
        <article class="ws-temp-variation-popup">
            <span>Pioggia satellite · GPM IMERG</span>
            <h3>${cell.mm.toFixed(1)} mm<small>nel giorno</small></h3>
            <dl>
                <div><dt>Giorno</dt><dd>${escapeHtml(date || '–')}</dd></div>
                ${pl !== null ? `<div><dt>Prob. liquida</dt><dd>${pl}%</dd></div>` : ''}
            </dl>
            <small style="opacity:.7">NASA/JAXA GPM IMERG Late · cella 0,1° (~11 km)</small>
        </article>`;
}

async function refreshImergRain() {
    const seq = ++state.seq.imergRain;
    const d = await fetchJson(IMERG_URL + '?_=' + Math.floor(Date.now() / 3600000), 12000);
    if (seq !== state.seq.imergRain || !state.enabled.imergRain) return;
    if (!ensureLayer('imergRain')) return;
    const date = d.date;
    const features = [];
    (d.grid || []).forEach(cell => {
        const lat = finite(cell.lat), lon = finite(cell.lon), mm = finite(cell.mm);
        if (lat === null || lon === null || mm === null || mm < 0.1) return;  // solo celle bagnate
        features.push(pointFeature(lat, lon, {
            r: Math.max(4, Math.min(10, 3 + Math.sqrt(mm))), fill: imergRainColor(mm),
            stroke: 'rgba(255,255,255,0.5)', sw: 0.5, fillOp: 0.75,
        }, `${mm.toFixed(1)} mm`, imergPopup(cell, date), 260));
    });
    setData('imergRain', features);
}

// ---------------------------------------------------------------------------
// dispatcher refresh + API pubblica
// ---------------------------------------------------------------------------

const REFRESHERS = {
    pollen: refreshPollen,
    air: refreshAir,
    quakes: refreshQuakes,
    airObs: refreshAirObs,
    tideGauges: refreshTideGauges,
    rainReports: refreshRainReports,
    territory: refreshTerritory,
    edoDrought: refreshEdoDrought,
    emsActivations: refreshEmsActivations,
    aeronetDust: refreshAeronetDust,
    imergRain: refreshImergRain,
};

function refresh(key) {
    const fn = REFRESHERS[key];
    return fn ? fn() : Promise.resolve();
}

export function initDerivedPoints(map, { beforeId } = {}) {
    state.map = map;
    state.beforeId = beforeId;
}

export function setDerivedEnabled(key, on) {
    if (!REFRESHERS[key] || !state.map) return;
    on = !!on;
    if (on === state.enabled[key]) return;
    state.enabled[key] = on;
    if (on) {
        // pollen/air/quakes (e gli altri one-shot) rifetchano ad ogni riattivazione
        refresh(key).catch(err => console.warn(`[wsv2] derived '${key}': fetch fallita`, err));
        if (key === 'territory') {
            if (state.territoryTimer) clearInterval(state.territoryTimer);
            state.territoryTimer = setInterval(() => {
                if (!document.hidden && state.enabled.territory) refreshTerritory();
            }, 300000);
        }
        if (key === 'rainReports') {
            if (state.rainTimer) clearInterval(state.rainTimer);
            state.rainTimer = setInterval(() => {
                if (!document.hidden && state.enabled.rainReports) refreshRainReports().catch(() => {});
            }, RAIN_REFRESH_MS);
        }
    } else {
        state.seq[key]++;  // invalida fetch in volo
        if (key === 'territory') {
            clearInterval(state.territoryTimer); state.territoryTimer = null;
            const status = document.getElementById('territory-layer-status');
            if (status) status.textContent = '';
        }
        if (key === 'rainReports' && state.rainTimer) {
            clearInterval(state.rainTimer);
            state.rainTimer = null;
        }
        clearLayer(key);
        if (hoverPopup) hoverPopup.remove();
        if (clickPopup) { clickPopup.remove(); clickPopup = null; }
    }
}

export function setPollenSpecies(s) {
    state.pollenSpecies = s || 'all';
    // il cambio specie rifetcha (l'endpoint filtra lato server, come la v1)
    if (state.enabled.pollen) refreshPollen().catch(() => {});
}

export function setAirPollutant(p) {
    state.airPollutant = p || 'aqi';
    // un solo fetch a monte: il cambio inquinante ri-renderizza soltanto (v1)
    if (state.enabled.air) renderAirMarkers();
}

export function setQuakesDays(n) {
    state.quakesDays = Number(n) || 7;
    if (state.enabled.quakes) refreshQuakes().catch(() => {});
}
